import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './app.css'
import { App } from './App'
import { sync, requestPersistence } from './sync/doc'
import { store } from './store/store'
import { me } from './store/me'
import { installViewportTracking } from './lib/viewport'
import { drainUnfurlQueue } from './lib/unfurl'
import { kickUploads } from './lib/uploads'
import { applyCachedTextSize } from './lib/scale'
import { applyStoredSidebarWidth } from './lib/sidebar'
import { pruneBlobs, takeFailures } from './lib/uploadQueue'
import { actions } from './store/actions'
import { displayTitle } from '@terrazzo/core'
import { showToast } from './store/toasts'

installViewportTracking()

if (import.meta.env.DEV) {
  void Promise.all([import('./store/actions'), import('./store/undo'), import('@terrazzo/core'), import('./lib/diagram')]).then(([a, u, core, dg]) => {
    ;(window as unknown as Record<string, unknown>).__tz = { store, doc: sync.doc, sync, actions: a.actions, undo: u, core, diagrams: dg.diagramActions }
  })
}

/**
 * Uploads the server refused for good (too big / wrong type) are reported and their
 * attachment removed; once synced, the local file cache is trimmed to live attachments.
 */
function watchUploads() {
  const report = async () => {
    for (const f of await takeFailures()) {
      if (f.variant !== 'full') continue
      const it = store.get(f.itemId)
      if (it?.attachments.some((a) => a.id === f.attId)) actions.removeAttachment(f.itemId, f.attId, { toast: false })
      const where = it ? ` to “${displayTitle(it.title, (x) => store.get(x)?.title) || 'Untitled'}”` : ''
      showToast(`Couldn't upload a file${where}: ${f.status === 413 ? 'it’s too large' : 'that file type isn’t supported'}.`, { timeout: 10_000 })
    }
  }
  window.addEventListener('terrazzo:upload-failed', () => void report())
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && void report())
  void report()
  let pruned = false
  const unsub = sync.subscribe(() => {
    if (pruned || !sync.getStatus().synced) return
    pruned = true
    unsub?.()
    const live = new Set<string>()
    for (const it of store.snaps.values()) for (const a of it.attachments) (live.add(a.key), a.thumbKey && live.add(a.thumbKey))
    void pruneBlobs(live).catch(() => {})
  })
}

applyCachedTextSize()
applyStoredSidebarWidth()

async function boot() {
  // 1. Local replica first: the UI renders from IndexedDB, never waiting on the network.
  await sync.openLocal().catch((e) => console.error('IndexedDB unavailable', e))
  store.rebuild()
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )

  // 2. Identity (cached for offline launches), then connect the socket.
  const state = await me.refresh()
  if (state.user) {
    sync.connect(state.user)
    void requestPersistence()
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    fetch('/api/timezone', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ tz }) }).catch(() => {})
    void drainUnfurlQueue()
    void kickUploads()
  }
  sync.subscribe(() => {
    if (sync.getStatus().authFailed) {
      void me.refresh()
    }
  })
  watchUploads()

  // 3. Service worker (app shell, thumbnails, upload queue, push, share target).
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
      const promptUpdate = (w: ServiceWorker) =>
        showToast('A new version is ready', {
          timeout: 60_000,
          action: {
            label: 'Reload',
            run: () => {
              w.postMessage('SKIP_WAITING')
            },
          },
        })
      if (reg.waiting && navigator.serviceWorker.controller) promptUpdate(reg.waiting)
      reg.addEventListener('updatefound', () => {
        const w = reg.installing
        w?.addEventListener('statechange', () => {
          if (w.state === 'installed' && navigator.serviceWorker.controller) promptUpdate(w)
        })
      })
      let reloading = false
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloading) return
        reloading = true
        location.reload()
      })
      // Look for a new version hourly and whenever the app comes back to the foreground
      // (installed phone apps are mostly backgrounded, where timers don't run).
      setInterval(() => void reg.update(), 60 * 60 * 1000)
      let lastCheck = Date.now()
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible' || Date.now() - lastCheck < 60_000) return
        lastCheck = Date.now()
        void reg.update().catch(() => {})
      })
    } catch (e) {
      console.warn('SW registration failed', e)
    }
  }
}

void boot()
